import { useAtom, useAtomValue } from "jotai";
import { WritableAtom } from "jotai";
import { PeriodKind } from "@/entities/Labeling/Account.model";
import {
    PeriodSelectorMenu,
    PeriodCategory,
    PeriodOption,
} from "./PeriodSelectorMenu";
import {
    summaryIncomeStatementEarliestDateAtom,
    summaryBalanceSheetEarliestDateAtom,
    summaryIncomeStatementAsOfDateAtom,
    summaryBalanceSheetAsOfDateAtom,
} from "../metrics.state";
import { MetricsType } from "../metrics.types";
import dayjs from "dayjs";
import { useMemo } from "react";

const MONTHLY_OPTIONS_HISTORICAL: PeriodOption[] = [
    { id: "ytd", label: "Year to Date" },
    { id: "l3m", label: "Last 3 Months" },
    { id: "l6m", label: "Last 6 Months" },
    { id: "l12m", label: "Last 12 Months" },
    { id: "l24m", label: "Last 24 Months" },
    { id: "l36m", label: "Last 36 Months" },
    { id: "l48m", label: "Last 48 Months" },
    { id: "l60m", label: "Last 60 Months" },
    { id: "all", label: "All Available Data" },
];

const QUARTERLY_OPTIONS_HISTORICAL: PeriodOption[] = [
    { id: "ytd", label: "Year to Date" },
    { id: "l4q", label: "Last 4 Quarters" },
    { id: "l8q", label: "Last 8 Quarters" },
    { id: "l12q", label: "Last 12 Quarters" },
    { id: "l16q", label: "Last 16 Quarters" },
    { id: "l20q", label: "Last 20 Quarters" },
    { id: "all", label: "All Available Data" },
];

const ANNUAL_OPTIONS_HISTORICAL: PeriodOption[] = [
    { id: "l3y", label: "Last 3 Years" },
    { id: "l5y", label: "Last 5 Years" },
    { id: "all", label: "All Available Data" },
];

const MONTHLY_OPTIONS_PROJECTION: PeriodOption[] = [
    { id: "l3m", label: "Next 3 Months" },
    { id: "l6m", label: "Next 6 Months" },
    { id: "l12m", label: "Next 12 Months" },
    { id: "l24m", label: "Next 24 Months" },
    { id: "l36m", label: "Next 36 Months" },
    { id: "l48m", label: "Next 48 Months" },
    { id: "l60m", label: "Next 60 Months" },
    { id: "all", label: "All Available Data" },
];

const QUARTERLY_OPTIONS_PROJECTION: PeriodOption[] = [
    { id: "l4q", label: "Next 4 Quarters" },
    { id: "l8q", label: "Next 8 Quarters" },
    { id: "l12q", label: "Next 12 Quarters" },
    { id: "l16q", label: "Next 16 Quarters" },
    { id: "l20q", label: "Next 20 Quarters" },
    { id: "all", label: "All Available Data" },
];

const ANNUAL_OPTIONS_PROJECTION: PeriodOption[] = [
    { id: "l3y", label: "Next 3 Years" },
    { id: "l5y", label: "Next 5 Years" },
    { id: "all", label: "All Available Data" },
];

const CATEGORIES_HISTORICAL: PeriodCategory[] = [
    { id: "monthly", label: "Monthly", options: MONTHLY_OPTIONS_HISTORICAL },
    {
        id: "quarterly",
        label: "Quarterly",
        options: QUARTERLY_OPTIONS_HISTORICAL,
    },
    { id: "annual", label: "Annual", options: ANNUAL_OPTIONS_HISTORICAL },
];

const CATEGORIES_PROJECTION: PeriodCategory[] = [
    { id: "monthly", label: "Monthly", options: MONTHLY_OPTIONS_PROJECTION },
    {
        id: "quarterly",
        label: "Quarterly",
        options: QUARTERLY_OPTIONS_PROJECTION,
    },
    { id: "annual", label: "Annual", options: ANNUAL_OPTIONS_PROJECTION },
];

interface PeriodSelectorProps {
    resolutionAtom: WritableAtom<
        { periodKind: PeriodKind; window?: string },
        [{ periodKind: PeriodKind; window?: string }],
        void
    >;
    variant?: "historical" | "projection";
    metricsType?: MetricsType;
}

export function PeriodSelector({
    resolutionAtom,
    variant = "historical",
    metricsType = MetricsType.INCOME_STATEMENT,
}: PeriodSelectorProps) {
    const [metricResolution, setMetricResolution] = useAtom(resolutionAtom);
    const incomeStatementEarliestDate = useAtomValue(
        summaryIncomeStatementEarliestDateAtom,
    );
    const balanceSheetEarliestDate = useAtomValue(
        summaryBalanceSheetEarliestDateAtom,
    );
    const incomeStatementAsOfDate = useAtomValue(
        summaryIncomeStatementAsOfDateAtom,
    );
    console.log("incomestatementEarliestDate", incomeStatementEarliestDate);
    const balanceSheetAsOfDate = useAtomValue(summaryBalanceSheetAsOfDateAtom);

    const earliestDate =
        metricsType === MetricsType.BALANCE_SHEET
            ? balanceSheetEarliestDate
            : incomeStatementEarliestDate;

    const asOfDate =
        metricsType === MetricsType.BALANCE_SHEET
            ? balanceSheetAsOfDate
            : incomeStatementAsOfDate;

    const calculateAvailableMonths = useMemo(() => {
        if (!earliestDate || !asOfDate) return null;
        const earliest = dayjs(earliestDate);
        const latest = dayjs(asOfDate);
        return latest.diff(earliest, "month") + 1;
    }, [earliestDate, asOfDate]);

    const isOptionDisabled = (optionId: string): boolean => {
        if (
            variant === "projection" ||
            optionId === "all" ||
            optionId === "ytd"
        )
            return false;
        if (!calculateAvailableMonths) return true;

        const requiredMonths: Record<string, number> = {
            l3m: 3,
            l6m: 6,
            l12m: 12,
            l24m: 24,
            l36m: 36,
            l48m: 48,
            l60m: 60,
            l4q: 12,
            l8q: 24,
            l12q: 36,
            l16q: 48,
            l20q: 60,
            l3y: 36,
            l5y: 60,
        };

        const required = requiredMonths[optionId];
        if (!required) return false;

        return calculateAvailableMonths < required;
    };

    const CATEGORIES_WITH_DISABLED = useMemo(() => {
        const categories =
            variant === "projection"
                ? CATEGORIES_PROJECTION
                : CATEGORIES_HISTORICAL;

        return categories.map((category) => ({
            ...category,
            options: category.options.map((option) => ({
                ...option,
                disabled: isOptionDisabled(option.id),
            })),
        }));
    }, [variant, calculateAvailableMonths]);

    const CATEGORIES = CATEGORIES_WITH_DISABLED;

    const getCurrentCategoryId = (): string => {
        const { periodKind } = metricResolution;
        if (periodKind === PeriodKind.MONTHLY) return "monthly";
        if (periodKind === PeriodKind.QUARTERLY) return "quarterly";
        if (periodKind === PeriodKind.YEARLY) return "annual";
        return "monthly";
    };

    const getCurrentOptionId = (): string => {
        const { periodKind, window } = metricResolution;

        if (window) return window;

        if (periodKind === PeriodKind.MONTHLY) return "l12m";
        if (periodKind === PeriodKind.QUARTERLY) return "l4q";
        if (periodKind === PeriodKind.YEARLY) return "all";
        return "l12m";
    };

    const getDisplayLabel = (): string => {
        const categoryId = getCurrentCategoryId();
        const optionId = getCurrentOptionId();

        const category = CATEGORIES.find((c) => c.id === categoryId);
        const option = category?.options.find((o) => o.id === optionId);

        if (category && option) {
            return `${category.label} - ${option.label}`;
        }

        return "Select Period";
    };

    const handleSelect = (categoryId: string, optionId: string) => {
        let periodKind: PeriodKind = PeriodKind.MONTHLY;

        if (categoryId === "monthly") {
            periodKind = PeriodKind.MONTHLY;
        } else if (categoryId === "quarterly") {
            periodKind = PeriodKind.QUARTERLY;
        } else if (categoryId === "annual") {
            periodKind = PeriodKind.YEARLY;
        }

        setMetricResolution({
            periodKind,
            window: optionId,
        });
    };

    return (
        <PeriodSelectorMenu
            categories={CATEGORIES}
            selectedCategoryId={getCurrentCategoryId()}
            selectedOptionId={getCurrentOptionId()}
            onSelect={handleSelect}
            displayLabel={getDisplayLabel()}
        />
    );
}
